/* eslint-disable @typescript-eslint/no-explicit-any */
import useUrlState from '@ahooksjs/use-url-state';
import keyBy from 'lodash/keyBy';
import moment from 'moment';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, SemanticWIDTHS } from 'semantic-ui-react';

import { Workshop, WorkshopSession } from '../../../../../types/workshop.types';
import './TabList.scss';
import TabDate from './components/TabDate';
import TabDay from './components/TabDay';

type ListTemplate = {
  variant?: string;
  minHeight?: number;
};

type Group = {
  id: string;
  label: string;
  items: Workshop[] | WorkshopSession[];
};

type TabListsProps = {
  groups?: Group[];
  groupByConfig: { type: string; subGroupBy?: string };
  template?: ListTemplate;
  filteredWorkshopIds?: string[];
  itemProps: any;
  iconConfig: any;
  unfilteredGroups?: Group[];
};

function getDefaultActiveTab(
  groups: Group[],
  groupByConfig: { type: string; subGroupBy?: string },
) {
  if (!groupByConfig?.type || !groups.length || groupByConfig.type !== 'date') return groups[0]?.id;
  const now = moment();
  const dates = groups.map((day) => day.id);
  const nextOrToday = dates.find((date) => moment(date).isSameOrAfter(now, 'day'));
  // If we find one, return it; otherwise, return the last date (all dates are in the past);
  return nextOrToday || dates[dates.length - 1];
}

const TabList: FC<TabListsProps> = (props) => {
  const {
    groups = [],
    filteredWorkshopIds,
    itemProps,
    template,
    groupByConfig,
    unfilteredGroups = groups,
  } = props;
  const { t } = useTranslation();
  const [urlState, setUrlState] = useUrlState(
    { tab: getDefaultActiveTab(groups, groupByConfig) },
    { navigateMode: 'replace' },
  );
  const { tab } = urlState;

  const groupsById = keyBy(groups, 'id');
  const activeGroup = groupsById[tab];

  return (
    <div className="workshops--container workshops--container--tabs">
      <Menu pointing secondary fluid widths={unfilteredGroups.length as SemanticWIDTHS}>
        {unfilteredGroups.map(({ id, label }) => (
          <Menu.Item key={id} active={id === tab} onClick={() => setUrlState({ tab: id })}>
            {groupByConfig?.type === 'date' ? (
              <TabDate value={id} count={groupsById[id]?.items?.length || 0} />
            ) : (
              label
            )}
          </Menu.Item>
        ))}
      </Menu>
      {!!activeGroup && (
        <TabDay
          id={activeGroup.id}
          items={activeGroup.items}
          filteredWorkshopIds={filteredWorkshopIds}
          template={template}
          itemProps={itemProps}
          groupBy={groupByConfig.subGroupBy}
        />
      )}
      {!activeGroup?.items?.length && (
        <div className="workshops--container--tabs-empty">{t('workshops.empty')}</div>
      )}
    </div>
  );
};

TabList.defaultProps = {
  itemProps: undefined,
  filteredWorkshopIds: undefined,
  template: { variant: 'card' },
  groups: [],
};

export default TabList;
